import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useAccessToken, useViewport } from '@chilecompra/react-kit'
import { Skeleton, Tooltip, Typography } from '@chilecompra/react-kit/components'
import { REACT_APP_PROVIDERS_BASE_URL } from '../../config/settings/environment'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { EXPIRES_AT, PURCHASE_ORDER_STATUS_DICTIONARY } from '../../config/settings/constants'
import {
  Card,
  Divider,
  ExclamationIcon,
  GridContainer,
  GridItem,
  Label,
  LetterAbilityAffidavit,
  LetterBlue,
  LinkLabel,
  Strong,
  TooltipText,
  WorkIcon
} from './QuoteBid.styles'

/**
 * The Quote Bid Selected's component.
 */
const QuoteBidSelected = props => {
  const {
    firstRow,
    loading,
    onAbilityAffidavit,
    onSeeDetail,
    openPurchaseOrderStatus,
    purchaseOrder,
    selectedProvider,
    typeCurrency
  } = props

  const { size } = useViewport()
  const { isBuyer, isSeller } = useAuthorization()
  const accessToken = useAccessToken()

  const handleSeeDetail = () => onSeeDetail(selectedProvider.bidId)
  const handleAbilityAffidavit = () => onAbilityAffidavit(selectedProvider.bidId)
  const handleSubmit =
    ({ id, code }) =>
    () => {
      openPurchaseOrderStatus({ id, code })
    }
  const checkPurchaseOrder = useCallback(
    idState =>
      (isBuyer && idState !== PURCHASE_ORDER_STATUS_DICTIONARY.SAVED) ||
      (isSeller &&
        (idState === PURCHASE_ORDER_STATUS_DICTIONARY.ACCEPTED ||
          idState === PURCHASE_ORDER_STATUS_DICTIONARY.CANCELLED_BUYER ||
          idState === PURCHASE_ORDER_STATUS_DICTIONARY.CANCELLED ||
          idState !== PURCHASE_ORDER_STATUS_DICTIONARY.SAVED ||
          idState === PURCHASE_ORDER_STATUS_DICTIONARY.REMOVED)),
    [isBuyer, isSeller]
  )
  const providerFileUrl = useMemo(
    () =>
      isBuyer
        ? `${REACT_APP_PROVIDERS_BASE_URL}/ficha/callback?access_token=${accessToken}&expires_in=${EXPIRES_AT}&rut=${selectedProvider.dni}`
        : `${REACT_APP_PROVIDERS_BASE_URL}/ficha/${selectedProvider.dni}`,
    [isBuyer, selectedProvider.dni]
  )

  return (
    <>
      <GridContainer padding={firstRow ? '41px 0 20px' : '16px 0 20px'}>
        {firstRow && (
          <GridItem xs={12}>
            <Typography color="black1" fontSize="22px" fontWeight="bold" lineHeight="26px" variant="h3">
              Proveedor seleccionado
            </Typography>
          </GridItem>
        )}
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <Card active enableBorder color="blank">
            <GridContainer alignItems="center" justifyContent="center" padding="0 0 25px" spacing={3}>
              <GridItem sm={3} xs={12}>
                <GridContainer>
                  <GridItem xs={12}>
                    <Typography color="black3" fontSize="12px" lineHeight="16px" variant="caption">
                      <Label>{loading ? <Skeleton /> : selectedProvider.companySize}</Label>
                    </Typography>
                  </GridItem>
                  <GridItem xs={12}>
                    <LetterBlue pointer href={providerFileUrl} target="_blank">
                      {loading ? <Skeleton /> : selectedProvider.name}
                    </LetterBlue>
                  </GridItem>
                  <GridItem xs={12}>
                    <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body2">
                      {loading ? <Skeleton /> : selectedProvider.dni}
                    </Typography>
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem sm={5} xs={12}>
                <GridContainer>
                  <GridItem xs={12}>
                    <Typography color="black1" fontSize="14px" lineHeight="18px" margin="4px 0 8px" variant="body2">
                      {loading ? <Skeleton /> : selectedProvider.description}
                    </Typography>
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12}>
                    <Typography color="gray1" fontSize="12px" fontWeight="bold" lineHeight="16px" variant="caption">
                      VIGENCIA: {loading ? <Skeleton /> : selectedProvider.validityDate}
                    </Typography>
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem sm xs={12}>
                <GridContainer padding="0 16px 0 0">
                  <GridItem xs={12}>
                    <Typography
                      align={size.isDownToTiny ? 'left' : 'right'}
                      color="black1"
                      fontSize="22px"
                      fontWeight="bold"
                      lineHeight="26px"
                      variant="h3"
                    >
                      {loading ? (
                        <Skeleton />
                      ) : (
                        selectedProvider.totalAmount &&
                        formatTypeCurrency(typeCurrency, selectedProvider.totalAmount.replace(/,/g, ''), true)
                      )}
                    </Typography>
                  </GridItem>
                </GridContainer>
                <GridContainer padding="0 16px 0 0">
                  <GridItem xs={12}>
                    <Typography
                      align={size.isDownToTiny ? 'left' : 'right'}
                      color="black3"
                      fontSize="14px"
                      lineHeight="18px"
                      variant="body2"
                    >
                      Monto total
                    </Typography>
                  </GridItem>
                </GridContainer>
              </GridItem>
              <Divider flexItem orientation="vertical" />
              <GridItem container sm alignItems="center" xs={12}>
                <GridContainer>
                  <GridItem xs={12}>
                    <LinkLabel pointer color="blue" onClick={handleSeeDetail}>
                      Ver detalle
                    </LinkLabel>
                  </GridItem>
                  <GridItem padding="4px 0 0" xs={12}>
                    <LetterAbilityAffidavit onClick={handleAbilityAffidavit}>
                      Declaración de habilidad
                    </LetterAbilityAffidavit>
                  </GridItem>
                  {purchaseOrder &&
                    purchaseOrder.map(
                      po =>
                        po.isActive && (
                          <GridItem container alignItems="center" key={String(po.id)} padding="4px 0 0" xs={12}>
                            <WorkIcon color={checkPurchaseOrder(po.idState) ? 'blue' : 'gray'} margin="0 5px 0 0" />
                            <LinkLabel
                              color={checkPurchaseOrder(po.idState) ? 'blue' : 'gray'}
                              pointer={checkPurchaseOrder(po.idState)}
                              onClick={
                                checkPurchaseOrder(po.idState) ? handleSubmit({ id: po.id, code: po.code }) : undefined
                              }
                            >
                              Orden de compra
                            </LinkLabel>
                            {!checkPurchaseOrder(po.idState) && (
                              <Tooltip
                                content={
                                  <TooltipText>
                                    El organismo público aún{' '}
                                    <Strong>no envía la orden de compra al proveedor seleccionado</Strong>. Una vez
                                    enviada, será visible desde esta opción.
                                  </TooltipText>
                                }
                              >
                                <ExclamationIcon margin="0 0 0 5px" />
                              </Tooltip>
                            )}
                          </GridItem>
                        )
                    )}
                </GridContainer>
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12}>
                <Divider />
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12}>
                <Typography color="black1" fontSize="14px" lineHeight="18px" margin="20px auto 0">
                  {loading ? (
                    <Skeleton />
                  ) : (
                    <>
                      <Strong>Justificación de selección indicada por el comprador</Strong>
                      <span>: {selectedProvider.selectionCriteria}</span>
                    </>
                  )}
                </Typography>
              </GridItem>
            </GridContainer>
          </Card>
        </GridItem>
      </GridContainer>
    </>
  )
}

QuoteBidSelected.defaultProps = {
  selectedProvider: PropTypes.shape({
    businessName: '',
    companySize: '',
    name: '',
    dni: ''
  })
}

QuoteBidSelected.propTypes = {
  firstRow: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onAbilityAffidavit: PropTypes.func.isRequired,
  onSeeDetail: PropTypes.func.isRequired,
  openPurchaseOrderStatus: PropTypes.func.isRequired,
  purchaseOrder: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      glossState: PropTypes.string.isRequired,
      idState: PropTypes.number.isRequired,
      messageStatusSaved: PropTypes.string
    }).isRequired
  ).isRequired,
  selectedProvider: PropTypes.shape({
    branchCode: PropTypes.string.isRequired,
    businessName: PropTypes.string,
    companyCode: PropTypes.string.isRequired,
    companySize: PropTypes.string,
    description: PropTypes.string.isRequired,
    bidId: PropTypes.number.isRequired,
    isSelectedOffer: PropTypes.number.isRequired,
    isSwornDeclaration: PropTypes.number.isRequired,
    name: PropTypes.string,
    dni: PropTypes.string,
    selectionCriteria: PropTypes.string,
    totalAmount: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired
  }).isRequired,
  typeCurrency: PropTypes.string.isRequired
}

export default QuoteBidSelected
